'use client'

import { useRef, useEffect, useCallback } from 'react'
import { useTranslations } from 'next-intl'
import gsap from 'gsap'

interface Props {
  onClick: () => void
  compact?: boolean   // smaller variant for mobile menu
}

const OLIVE      = '#5B7005'
const OLIVE_DARK = '#4A5D04'
const LIME       = '#9FAF08'
const CREAM      = '#F5ECD8'

export default function ContactButton({ onClick, compact = false }: Props) {
  const t = useTranslations('nav')
  const btnRef   = useRef<HTMLButtonElement>(null)
  const dotRef   = useRef<HTMLSpanElement>(null)
  const ringRef  = useRef<HTMLSpanElement>(null)
  const breathTlRef = useRef<gsap.core.Timeline | null>(null)
  const ringTlRef   = useRef<gsap.core.Timeline | null>(null)

  // Start pulsing dot + ring ping; pause when tab hidden
  useEffect(() => {
    const dot = dotRef.current
    const ring = ringRef.current
    if (!dot || !ring) return

    const breath = gsap.timeline({ repeat: -1, yoyo: true })
      .to(dot, {
        scale: 1.2,
        opacity: 0.7,
        boxShadow: `0 0 14px ${LIME}`,
        duration: 1,
        ease: 'sine.inOut',
      })
    breathTlRef.current = breath

    const ringTl = gsap.timeline({ repeat: -1, repeatDelay: 4.8 })
      .set(ring,  { scale: 1, opacity: 0.6 })
      .to(ring, {
        scale: 3, opacity: 0,
        duration: 1.2, ease: 'power2.out',
      })
    ringTlRef.current = ringTl

    const onVisibility = () => {
      const paused = document.hidden
      breath.paused(paused)
      ringTl.paused(paused)
    }
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      breath.kill()
      ringTl.kill()
    }
  }, [])

  const handleEnter = useCallback(() => {
    const btn = btnRef.current
    if (!btn) return
    gsap.to(btn, {
      y: -2,
      backgroundColor: OLIVE_DARK,
      boxShadow: '0 4px 16px rgba(91,112,5,0.25)',
      duration: 0.2, ease: 'power2.out',
    })
    // Speed up pulse
    breathTlRef.current?.timeScale(2.5)
    ringTlRef.current?.timeScale(2)
  }, [])

  const handleLeave = useCallback(() => {
    const btn = btnRef.current
    if (!btn) return
    gsap.to(btn, {
      y: 0,
      backgroundColor: OLIVE,
      boxShadow: '0 0 0 rgba(91,112,5,0)',
      duration: 0.25, ease: 'power2.out',
    })
    breathTlRef.current?.timeScale(1)
    ringTlRef.current?.timeScale(1)
  }, [])

  const spawnClickParticles = useCallback(() => {
    const btn = btnRef.current
    if (!btn) return
    const r = btn.getBoundingClientRect()
    const cx = r.left + r.width * 0.2    // near the dot
    const cy = r.top  + r.height / 2
    for (let i = 0; i < 4; i++) {
      const p = document.createElement('div')
      p.style.cssText = `
        position: fixed;
        left: ${cx}px; top: ${cy}px;
        width: 4px; height: 4px;
        border-radius: 50%;
        background: ${LIME};
        box-shadow: 0 0 6px ${LIME};
        pointer-events: none;
        z-index: 200;
      `
      document.body.appendChild(p)
      gsap.set(p, { xPercent: -50, yPercent: -50 })
      gsap.to(p, {
        y: -(20 + Math.random() * 30),
        x: (Math.random() - 0.5) * 24,
        opacity: 0,
        duration: 0.4 + Math.random() * 0.2,
        ease: 'power2.out',
        onComplete: () => p.remove(),
      })
    }
  }, [])

  const handleClick = useCallback(() => {
    const btn = btnRef.current
    if (btn) {
      gsap.to(btn, { scale: 0.96, duration: 0.08, yoyo: true, repeat: 1 })
    }
    spawnClickParticles()
    onClick()
  }, [onClick, spawnClickParticles])

  const btnH = compact ? 36 : 40
  const fontSize = compact ? 13 : 14

  return (
    <button
      ref={btnRef}
      type="button"
      onClick={handleClick}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        height: btnH,
        padding: '0 20px 0 14px',
        borderRadius: 999,
        border: 'none',
        background: OLIVE,
        color: CREAM,
        fontFamily: 'var(--font-body)',
        fontWeight: 500,
        fontSize,
        letterSpacing: '0.02em',
        cursor: 'pointer',
        willChange: 'transform, background-color, box-shadow',
      }}
    >
      {/* Status dot + ring */}
      <span
        aria-hidden="true"
        style={{
          position: 'relative',
          width: 6, height: 6,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span
          ref={ringRef}
          style={{
            position: 'absolute',
            width: 6, height: 6,
            borderRadius: '50%',
            border: `1px solid ${LIME}`,
            opacity: 0,
            pointerEvents: 'none',
          }}
        />
        <span
          ref={dotRef}
          style={{
            width: 6, height: 6,
            borderRadius: '50%',
            background: LIME,
            boxShadow: `0 0 8px ${LIME}`,
          }}
        />
      </span>
      <span>{t('contact')}</span>
    </button>
  )
}
